/**
 * useFirebaseServices - Hook for Firebase Analytics, Remote Config, and Messaging
 * 
 * Provides easy access to Firebase services throughout the app
 */
import { useEffect, useState, useCallback } from 'react';
import {
    fetchRemoteConfig,
    isKillSwitchActive,
    isMaintenanceMode,
    areAIFeaturesEnabled,
    isImageGenerationEnabled,
    getMaxDailyChats,
    requestNotificationPermission,
    onForegroundMessage,
    trackEvent,
    setAnalyticsUserId,
    setAnalyticsUserProperties
} from '@/lib/firebase';

/**
 * Hook for Remote Config
 * Fetches and provides real-time feature flags
 */
export const useRemoteConfig = () => {
    const [config, setConfig] = useState({
        killSwitch: false,
        maintenanceMode: false,
        aiEnabled: true,
        imageGenEnabled: true,
        maxDailyChats: 50,
        isLoading: true
    });

    useEffect(() => {
        const loadConfig = async () => {
            await fetchRemoteConfig();
            setConfig({
                killSwitch: isKillSwitchActive(),
                maintenanceMode: isMaintenanceMode(),
                aiEnabled: areAIFeaturesEnabled(),
                imageGenEnabled: isImageGenerationEnabled(),
                maxDailyChats: getMaxDailyChats(),
                isLoading: false
            });
        };
        
        loadConfig();
        
        // Refresh config every 5 minutes
        const interval = setInterval(loadConfig, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    return config;
};

/**
 * Hook for Push Notifications
 * Manages notification permission and FCM token
 */
export const usePushNotifications = () => {
    const [token, setToken] = useState(null);
    const [permission, setPermission] = useState(Notification?.permission || 'default');
    const [isSupported, setIsSupported] = useState(false);

    useEffect(() => {
        // Check if notifications are supported
        setIsSupported('Notification' in window && 'serviceWorker' in navigator);
    }, []);

    const requestPermission = useCallback(async () => {
        const fcmToken = await requestNotificationPermission();
        if (fcmToken) {
            setToken(fcmToken);
            setPermission('granted');
            return fcmToken;
        }
        setPermission(Notification?.permission || 'denied');
        return null;
    }, []);

    // Listen for foreground messages
    useEffect(() => {
        if (permission !== 'granted') return;
        
        const unsubscribe = onForegroundMessage((payload) => {
            // Show toast or in-app notification
            console.log('Received foreground message:', payload);
            
            // You can dispatch to a global notification state here
            // or use the sonner toast library
        });
        
        return unsubscribe;
    }, [permission]);

    return {
        token,
        permission,
        isSupported,
        requestPermission
    };
};

/**
 * Hook for Analytics
 * Provides tracking functions with user context
 */
export const useAnalytics = () => {
    const track = useCallback((eventName, params = {}) => {
        trackEvent(eventName, params);
    }, []);

    const setUser = useCallback((userId, properties = {}) => {
        setAnalyticsUserId(userId);
        if (Object.keys(properties).length > 0) {
            setAnalyticsUserProperties(properties);
        }
    }, []);

    return { track, setUser };
};

/**
 * Combined hook for all Firebase services
 */
export const useFirebaseServices = () => {
    const remoteConfig = useRemoteConfig();
    const pushNotifications = usePushNotifications();
    const analytics = useAnalytics();

    return {
        config: remoteConfig,
        notifications: pushNotifications,
        analytics
    };
};

export default useFirebaseServices;
